import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';
import Footer from '@/pages/home/components/Footer';
import logo from '@/images/logo.png'
import hero from '@/images/personal-hero.jpeg'

const faqItems = [
  {
    question: '¿Cuánto tiempo tarda la aprobación?',
    answer: 'Nuestro proceso de evaluación es rápido y transparente. Una vez que entregues tu documentación completa, recibirás una respuesta en un plazo máximo de 48 horas hábiles. En muchos casos, la aprobación puede darse en menos de 24 horas.',
  },
  {
    question: '¿Necesito un aval para solicitar el crédito?',
    answer: 'No. Una de las grandes ventajas de nuestro Crédito Personal es que no necesitas presentar un aval ni garantía hipotecaria. Evaluamos tu perfil con base en tu historial crediticio y capacidad de pago.',
  },
  {
    question: '¿Puedo liquidar mi crédito antes del plazo?',
    answer: 'Sí, y sin ninguna penalización. Puedes realizar pagos anticipados parciales o liquidar el total de tu crédito en cualquier momento sin cargos extra.',
  },
  {
    question: '¿Qué documentación necesito?',
    answer: 'Solo necesitas identificación oficial vigente (INE), comprobante de domicilio con máximo 3 meses de antigüedad y comprobantes de ingresos de los últimos 3 meses. Si eres trabajador independiente, también requerimos tu declaración fiscal más reciente.',
  },
  {
    question: '¿Cómo recibo el dinero una vez aprobado?',
    answer: 'El depósito se realiza directamente a tu cuenta bancaria. Una vez firmado el contrato, el dinero se refleja en tu cuenta en un plazo de 24 horas hábiles.',
  },
  {
    question: '¿Hay algún costo por solicitar el crédito?',
    answer: 'No. La solicitud y evaluación de tu crédito es completamente gratuita y sin compromiso. Solo pagarás los intereses correspondientes si tu crédito es aprobado y decides aceptarlo.',
  },
];

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'Sin aval',
    description: 'No necesitas presentar garantías ni aval. Tu historial crediticio y capacidad de pago son suficientes para evaluar tu solicitud.',
  },
  {
    icon: 'ri-percent-line',
    title: 'Tasa fija competitiva',
    description: 'Tu tasa de interés se mantiene fija durante todo el plazo del crédito. Sin sorpresas, sin ajustes. Siempre sabrás exactamente cuánto pagas.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Sin penalización por pago anticipado',
    description: 'Adelanta pagos o liquida tu crédito por completo cuando quieras. No cobramos comisiones por pago anticipado.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Seguro de vida incluido',
    description: 'Tu crédito incluye un seguro de vida para tu tranquilidad y la de tu familia.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Solicitud en línea',
    description: 'Llena el formulario con tus datos básicos. Es rápido, seguro y sin compromiso. Solo te tomará unos minutos.',
    icon: 'ri-file-list-3-line',
  },
  {
    number: '02',
    title: 'Evaluación',
    description: 'Nuestro equipo analiza tu perfil en 48 horas. Revisamos tu historial crediticio y capacidad de pago de forma transparente.',
    icon: 'ri-search-eye-line',
  },
  {
    number: '03',
    title: 'Aprobación y firma',
    description: 'Si tu crédito es aprobado, te enviaremos el contrato y listo.',
    icon: 'ri-pen-nib-line',
  },
  {
    number: '04',
    title: 'Depósito inmediato',
    description: 'Una vez firmado, el dinero se deposita en tu cuenta en máximo 24 horas. Directo, sin intermediarios.',
    icon: 'ri-bank-line',
  },
];

export default function CreditoPersonal() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px -20px 0px' }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen" style={{ background: 'oklch(var(--background-50))' }}>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'py-3' : 'py-5'
        }`}
        style={{
          background: scrolled ? 'rgba(255, 255, 255, 0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0, 0, 0, 0.06)' : '1px solid transparent',
        }}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="Crece Prestaciones"
              className="h-14 w-auto transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-300 whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line"></i>
              Volver al inicio
            </Link>
            <Link
              to="/#contacto"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-white text-sm font-semibold rounded-full hover:bg-accent-600 transition-all duration-300 hover:scale-[1.04] active:scale-[0.97] cursor-pointer whitespace-nowrap"
            >
              Solicitar ahora
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <Link
            to="/#contacto"
            className="md:hidden inline-flex items-center gap-2 px-4 py-2 bg-accent-500 text-white text-xs font-semibold rounded-full hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Solicitar ahora
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'oklch(var(--accent-50))' }}></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-20" style={{ background: 'oklch(var(--accent-300))' }}></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-15" style={{ background: 'oklch(var(--accent-400))' }}></div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 w-full">
              <ScrollReveal animation="fade-up" duration={600}>
                <div className="inline-flex items-center gap-3 mb-5">
                  <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                  <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Crédito Personal</span>
                  <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                </div>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={100} duration={600}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                  El crédito que se adapta{' '}
                  <span className="relative">
                    <span className="relative z-10 bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                      a tu vida
                    </span>
                  </span>
                </h1>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={200} duration={600}>
                <p className="text-lg text-foreground-500 leading-relaxed mb-8 max-w-xl">
                  Préstamos personales diseñados para ti. Sin aval y con tasa fija. Obtén el financiamiento que necesitas en tiempo récord.
                </p>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={300} duration={600}>
                <div className="flex flex-col sm:flex-row gap-4 mb-10">
                  <div className="flex-1 p-5 rounded-2xl" style={{ background: 'oklch(var(--background-50))' }}>
                    <span className="text-[10px] text-foreground-400 uppercase tracking-widest font-semibold">Monto del crédito</span>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      $5,000 – $50,000
                    </div>
                  </div>
                  <div className="flex-1 p-5 rounded-2xl" style={{ background: 'oklch(var(--background-50))' }}>
                    <span className="text-[10px] text-foreground-400 uppercase tracking-widest font-semibold">Plazo</span>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2" style={{ fontFamily: 'var(--font-heading)' }}>
                      Hasta 18 meses
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={400} duration={600}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link
                    to="/#contacto"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-white text-base font-semibold rounded-full hover:bg-accent-600 transition-all duration-300 hover:scale-[1.04] active:scale-[0.97] cursor-pointer whitespace-nowrap group/btn"
                  >
                    Solicitar mi crédito
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover/btn:translate-x-1"></i>
                  </Link>
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-accent-50 flex items-center justify-center text-[10px] text-foreground-500" style={{ background: 'oklch(var(--background-100))' }}>
                          <i className="ri-user-line"></i>
                        </div>
                      ))}
                    </div>
                    <span className="text-sm text-foreground-500">+1,500 clientes satisfechos</span>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="flex-1 w-full">
              <ScrollReveal animation="fade-up" delay={200} duration={700}>
                <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={hero}
                    alt="Crédito Personal - Libertad financiera"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex flex-wrap gap-3">
                      <span className="px-4 py-2 rounded-full text-xs font-semibold bg-white/90 text-foreground-900 backdrop-blur-sm whitespace-nowrap flex items-center gap-2">
                        <i className="ri-check-line text-accent-500 text-xs"></i>
                        Sin aval
                      </span>
                      <span className="px-4 py-2 rounded-full text-xs font-semibold bg-white/90 text-foreground-900 backdrop-blur-sm whitespace-nowrap flex items-center gap-2">
                        <i className="ri-check-line text-accent-500 text-xs"></i>
                        Tasa fija competitiva
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="relative py-20 md:py-28" style={{ background: 'oklch(var(--background-50))' }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <ScrollReveal animation="fade-up" duration={600}>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Beneficios</span>
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                Todo lo que tu crédito{' '}
                <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                  incluye
                </span>
              </h2>
              <p className="mt-4 text-foreground-500 text-base max-w-xl mx-auto">
                Cada Crédito Personal de Crece Prestaciones viene con estos beneficios pensados para darte tranquilidad y control total sobre tus finanzas.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((benefit, index) => (
              <ScrollReveal key={benefit.title} animation="fade-up" delay={index * 100} duration={600}>
                <div className="p-7 rounded-3xl h-full transition-all duration-500 group hover:-translate-y-1 border border-background-200/60" style={{ background: 'oklch(var(--background-50))' }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110" style={{ background: 'oklch(var(--accent-50))' }}>
                    <i className={`${benefit.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Detalles y Requisitos */}
      <section className="relative py-20 md:py-28" style={{ background: 'oklch(var(--background-100))' }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="flex-1">
              <ScrollReveal animation="fade-up" duration={600}>
                <div className="inline-flex items-center gap-3 mb-4">
                  <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                  <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Requisitos</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  Lo que necesitas para{' '}
                  <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                    empezar
                  </span>
                </h2>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={100} duration={600}>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-id-card-line', text: 'Identificación oficial vigente (INE)' },
                    { icon: 'ri-home-4-line', text: 'Comprobante de domicilio (máximo 3 meses de antigüedad)' },
                    { icon: 'ri-bank-card-line', text: 'Comprobantes de ingresos de los últimos 3 meses' },
                    { icon: 'ri-file-text-line', text: 'Estado de cuenta bancario para depósito' },
                  ].map((req, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 rounded-2xl transition-all duration-300 hover:bg-background-50" style={{ background: 'oklch(var(--background-50) / 0.5)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'oklch(var(--accent-50))' }}>
                        <i className={`${req.icon} text-accent-600`}></i>
                      </div>
                      <span className="text-foreground-700 text-sm pt-2.5">{req.text}</span>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>

            <div className="flex-1">
              <ScrollReveal animation="fade-up" delay={200} duration={600}>
                <div className="inline-flex items-center gap-3 mb-4">
                  <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                  <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Características</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  Diseñado para{' '}
                  <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                    ti
                  </span>
                </h2>
              </ScrollReveal>

              <ScrollReveal animation="fade-up" delay={300} duration={600}>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-flashlight-line', title: 'Respuesta en 48 horas', desc: 'Nuestro sistema de evaluación es ágil y eficiente. Obtén una respuesta clara sobre tu solicitud en máximo 2 días hábiles.' },
                    { icon: 'ri-lock-2-line', title: 'Datos protegidos', desc: 'Tu información personal y financiera está resguardada con los más altos estándares de seguridad y encriptación.' },
                    { icon: 'ri-customer-service-2-line', title: 'Asesoría personalizada', desc: 'Un asesor financiero te acompaña durante todo el proceso para resolver tus dudas y ayudarte a elegir la mejor opción.' },
                  ].map((feat, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 rounded-2xl transition-all duration-300 hover:bg-background-50" style={{ background: 'oklch(var(--background-50) / 0.5)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'oklch(var(--accent-50))' }}>
                        <i className={`${feat.icon} text-accent-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-950 text-sm mb-1">{feat.title}</h4>
                        <p className="text-foreground-500 text-sm leading-relaxed">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso Section */}
      <section className="relative py-20 md:py-28" style={{ background: 'oklch(var(--background-50))' }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <ScrollReveal animation="fade-up" duration={600}>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Proceso</span>
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                Así de{' '}
                <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                  simple
                </span>
                {' '}es obtenerlo
              </h2>
              <p className="mt-4 text-foreground-500 text-base max-w-xl mx-auto">
                Cuatro pasos y tu crédito está listo. Sin complicaciones, sin letras chiquitas.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            <div className="hidden lg:block absolute top-12 left-[15%] right-[15%] h-px" style={{ background: 'oklch(var(--background-300))' }}></div>

            {steps.map((step, index) => (
              <ScrollReveal key={step.number} animation="fade-up" delay={index * 120} duration={600}>
                <div className="relative text-center group">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center relative z-10 transition-all duration-300 group-hover:scale-105" style={{ background: 'oklch(var(--accent-50))' }}>
                    <i className={`${step.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-6xl font-bold text-accent-500/10" style={{ fontFamily: 'var(--font-heading)' }}>{step.number}</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-950 mb-2 relative z-10" style={{ fontFamily: 'var(--font-heading)' }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed relative z-10 max-w-xs mx-auto">
                    {step.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-20 md:py-28" style={{ background: 'oklch(var(--background-100))' }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <ScrollReveal animation="fade-up" duration={600}>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
                <span className="text-accent-600 text-sm font-semibold tracking-widest uppercase">Preguntas Frecuentes</span>
                <span className="w-8 h-px rounded-full bg-accent-500/30"></span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                Resolvemos tus{' '}
                <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                  dudas
                </span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto">
            {faqItems.map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 60} duration={500}>
                <div
                  className="mb-3 rounded-2xl overflow-hidden transition-all duration-300 border cursor-pointer"
                  style={{
                    background: 'oklch(var(--background-50))',
                    borderColor: openFaq === index ? 'oklch(var(--accent-300) / 0.6)' : 'oklch(var(--background-200) / 0.6)',
                  }}
                  onClick={() => toggleFaq(index)}
                >
                  <div className="flex items-center justify-between px-6 py-5">
                    <h4 className="text-sm font-semibold text-foreground-950 pr-6" style={{ fontFamily: 'var(--font-heading)' }}>
                      {item.question}
                    </h4>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      openFaq === index ? 'rotate-45' : ''
                    }`} style={{ background: 'oklch(var(--accent-50))' }}>
                      <i className="ri-add-line text-accent-600 text-sm"></i>
                    </div>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-6 text-sm text-foreground-500 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" style={{ background: 'oklch(var(--accent-50))' }}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-20" style={{ background: 'oklch(var(--accent-300))' }}></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-15" style={{ background: 'oklch(var(--accent-400))' }}></div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <ScrollReveal animation="fade-up" duration={600}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              ¿Listo para hacer crecer{' '}
              <span className="bg-gradient-to-r from-accent-600 to-accent-500 bg-clip-text text-transparent">
                lo que importa
              </span>
              ?
            </h2>
            <p className="text-lg text-foreground-500 mb-8 max-w-lg mx-auto">
              Da el primer paso hoy. Nuestro equipo está listo para ayudarte a encontrar el crédito perfecto para ti.
            </p>
          </ScrollReveal>

          <ScrollReveal animation="fade-up" delay={150} duration={600}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/#contacto"
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-white text-base font-semibold rounded-full hover:bg-accent-600 transition-all duration-300 hover:scale-[1.04] active:scale-[0.97] cursor-pointer whitespace-nowrap group/btn shadow-lg shadow-accent-500/25"
              >
                Solicitar mi crédito ahora
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover/btn:translate-x-1"></i>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-4 text-foreground-600 text-base font-semibold rounded-full hover:text-foreground-950 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Volver al inicio
                <i className="ri-arrow-left-line"></i>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}